import type { WebhookSubscription } from '@servicetim/shared';
import { useDeleteWebhook, useUpdateWebhook, useWebhooks } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  DropdownMenuItem,
  DropdownMenuSeparator,
  pagedTableProps,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { ListOrdered, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { DeliveriesDrawer } from '../../webhooks/deliveries-drawer';
import { formatDateTime } from '../common/format';
import { RowActions } from '../common/row-actions';
import { problemMessage, withProblemMessage } from '../common/server-errors';
import { WebhookDialog } from './webhook-dialog';

type Dialog = { kind: 'create' } | { kind: 'edit'; webhook: WebhookSubscription } | null;

export function WebhooksTable() {
  const webhooks = useWebhooks();
  const update = useUpdateWebhook();
  const remove = useDeleteWebhook();
  const confirm = useConfirm();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<WebhookSubscription | null>(null);

  const onDelete = useCallback(
    (webhook: WebhookSubscription) =>
      void confirm({
        title: `Delete ${webhook.name}?`,
        description: 'The endpoint stops receiving events. Delivery history is not kept yet.',
        confirmLabel: 'Delete',
        destructive: true,
        onConfirm: async () => {
          await withProblemMessage(() => remove.mutateAsync(webhook.id));
          toast.success(`${webhook.name} deleted`);
        },
      }),
    [confirm, remove.mutateAsync],
  );

  const toggleEnabled = useCallback(
    async (webhook: WebhookSubscription, enabled: boolean) => {
      try {
        await update.mutateAsync({ id: webhook.id, patch: { enabled } });
        toast.success(`${webhook.name} ${enabled ? 'enabled' : 'disabled'}`);
      } catch (error) {
        toast.error(problemMessage(error));
      }
    },
    [update.mutateAsync],
  );

  const columns = useMemo<ColumnDef<WebhookSubscription, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className="font-medium text-fg">{row.original.name}</span>,
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: ({ row }) => (
          <code className="block max-w-xs truncate font-mono text-xs" title={row.original.url}>
            {row.original.url}
          </code>
        ),
      },
      {
        id: 'events',
        header: 'Events',
        enableSorting: false,
        cell: ({ row }) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" tabIndex={0}>
                {row.original.eventTypes.length} event type
                {row.original.eventTypes.length === 1 ? '' : 's'}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{row.original.eventTypes.join(', ')}</TooltipContent>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'secret',
        header: 'Secret',
        enableSorting: false,
        cell: ({ row }) => <code className="font-mono text-xs">{row.original.secret}</code>,
      },
      {
        accessorKey: 'enabled',
        header: 'Enabled',
        cell: ({ row }) => (
          <Switch
            checked={row.original.enabled}
            aria-label={`${row.original.name} enabled`}
            disabled={update.isPending}
            onCheckedChange={(checked) => void toggleEnabled(row.original, checked)}
          />
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-fg-muted">{formatDateTime(row.original.updatedAt)}</span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        size: 48,
        cell: ({ row }) => (
          <RowActions label={`Actions for ${row.original.name}`}>
            <DropdownMenuItem onSelect={() => setDeliveriesFor(row.original)}>
              <ListOrdered /> Deliveries
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setDialog({ kind: 'edit', webhook: row.original })}>
              <Pencil /> Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => onDelete(row.original)}>
              <Trash2 /> Delete
            </DropdownMenuItem>
          </RowActions>
        ),
      },
    ],
    [update.isPending, onDelete, toggleEnabled],
  );

  return (
    <>
      <DataTable
        columns={columns}
        {...pagedTableProps(webhooks)}
        getRowId={(w) => w.id}
        caption="Webhook subscriptions"
        toolbar={
          <div className="flex justify-end">
            <Button onClick={() => setDialog({ kind: 'create' })}>
              <Plus /> New webhook
            </Button>
          </div>
        }
        emptyTitle="No webhooks"
        emptyDescription="Subscribe an HTTPS endpoint to ticket, comment and job events."
        emptyAction={
          <Button onClick={() => setDialog({ kind: 'create' })}>
            <Plus /> New webhook
          </Button>
        }
      />
      {dialog ? (
        <WebhookDialog
          webhook={dialog.kind === 'edit' ? dialog.webhook : null}
          onClose={() => setDialog(null)}
        />
      ) : null}
      <DeliveriesDrawer
        subscription={deliveriesFor}
        onOpenChange={(open) => (open ? undefined : setDeliveriesFor(null))}
      />
    </>
  );
}
